import './styles.css'
import ConfirmationPopup from 'components/CommonComponents/ConfirmationPopup'
import { Button } from 'components/ui'
import { VmAiEditor } from '../VmAiEditor'
import React from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, ArrowDown } from 'lucide-react'
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd'
import keys from 'constants/keys'
import { SubtaskPreviewCard } from '../SubtaskPreviewCard'
import { PreviewTitle } from '../SubtaskPreviewCard/PreviewTitle'
import { SubtaskDock } from '../SubtaskDock'
import { TaskNameStep } from '../TaskNameStep'
import { TaskLivePreview } from '../TaskLivePreview'
import { SubtaskBuilderEmptyState } from '../SubtaskBuilderEmptyState'
import { subtaskTypeKeys } from '../../model/subtaskTypes'
import { isRtlSelector } from 'redux/selectors/general'
import { Content, Subtask } from '../../model/types'
import { useSubtaskBuilder } from '../../hooks/useSubtaskBuilder'
import { AI_IMAGE_BUTTON_TYPE, useSubtaskMenu } from '../../hooks/useSubtaskMenu'
import TaskTitle from '../../Steps/SubtaskCreationStep/TaskTitle'
import { BuilderWidgetProvider } from '../../contexts/BuilderWidgetContext'
import { SubtaskEditor } from '../SubtaskEditor'
import { SubtaskCategoryHeader } from '../SubtaskCategoryHeader'
import { useCreationText } from '../../hooks/useCreationText'
import { WidgetIsRequiredSwitch } from '../../Steps/SubtaskCreationStep/Widgets/WidgetIsRequiredSwitch'

type Field = Subtask | Content
interface CategoryGrouping {
  controls: React.ReactNode
  editor?: React.ReactNode
  onSelectSubtask?: (index: number) => void
  onAddSubtask?: () => void
  onReorder: (result: DropResult) => void
  renderItems: (
    renderItem: (index: number, dragIndex?: number) => React.ReactNode,
    actions: {
      buttons: ReturnType<typeof useSubtaskMenu>['buttons']
      onAdd: (type: number) => void
      clearEditor: () => void
    }
  ) => React.ReactNode
  intro: (taskName: React.ReactNode) => React.ReactNode
  dockHint?: React.ReactNode
  activeCategory?: string
  previewGroups: { name: string; subtasks: { title: string; type: number }[] }[]
  canAddSubtasks: boolean
  validationMessage?: string
}
export function SubtaskBuilder({
  fields,
  categoryGrouping,
  headerSlot,
}: {
  fields: Field[]
  categoryGrouping?: CategoryGrouping
  headerSlot?: HTMLDivElement | null
}) {
  const t = useCreationText()
  const rtl = useSelector(isRtlSelector)
  const { buttons, addSubtask } = useSubtaskMenu()
  const {
    isEdit,
    values,
    errors,
    removalPending,
    requestRemove,
    confirmRemove,
    cancelRemove,
    sumbitStep,
    aiIds,
    selectedId,
    setSelectedId,
    editorRef,
    listRef,
    setAiGenerating,
    busy,
    selectedIndex,
    selected,
    selectedType,
    selectedAi,
    hasSubtasks,
    hasCreatedSubtask,
    hasStartedBuilding,
    startBuilding,
    titleRef,
    select,
    finish,
    reorder,
  } = useSubtaskBuilder(fields, !!categoryGrouping?.previewGroups.length)

  const SelectedIcon = buttons.find((button) => button.type === selectedType)?.Component
  const onboarding = !fields.length
  const naming = categoryGrouping
    ? !categoryGrouping.previewGroups.length
    : !hasStartedBuilding && !hasCreatedSubtask && onboarding
  const categoryEditor = !naming && categoryGrouping?.editor
  const nameReady = !!values.title?.trim()
  const addingDisabled =
    isEdit || busy || (onboarding && (naming || !nameReady)) || (!!categoryGrouping && !categoryGrouping.canAddSubtasks)
  const handleAddSubtask = (type: number) => {
    if (!addingDisabled) {
      categoryGrouping?.onAddSubtask?.()
      addSubtask(type)
    }
  }
  const selectedTitle =
    values.sub_tasks?.[selectedIndex]?.title ?? (selected && 'title' in selected ? selected.title : '')

  const selectedCategory =
    categoryGrouping && selectedType !== keys.SUBTASK_TYPES.DESCRIPTION
      ? values.sub_tasks?.[selectedIndex]?.category
      : undefined

  const errorCount = fields.filter((_, index) => errors.sub_tasks?.[index]).length
  const footerStatus = busy
    ? t.uploading
    : categoryGrouping?.validationMessage ||
      (!fields.length && !headerSlot
        ? t.addAtLeastOne
        : errorCount
        ? t.subtasksNeedAttention.replace('{count}', String(errorCount))
        : '')

  const taskTitle = (
    <div ref={titleRef} className={naming ? undefined : 'creation-task-title'}>
      <fieldset disabled={busy} style={{ width: '100%', minWidth: 0 }}>
        <TaskTitle
          showLabel
          label={naming ? t.nameYourTask : undefined}
          defaultValue={values.title || ''}
          formControlWidth="100%"
          textAlign="start"
          inputBoxShadow="none"
        />
      </fieldset>
    </div>
  )

  const nextAction = (
    <div className={headerSlot ? 'creation-header-action' : 'creation-builder-footer'}>
      {footerStatus && (
        <span className="creation-footer-status" role="status">
          {footerStatus}
        </span>
      )}
      <Button
        px={headerSlot ? '16px' : '24px'}
        py="0"
        borderRadius="9px"
        bg="#222222"
        fontWeight={600}
        _hover={{ bg: '#333333' }}
        variant="primary"
        height="36px"
        type="button"
        className="creation-next"
        isDisabled={busy || !hasSubtasks || !!categoryGrouping?.validationMessage}
        onClick={sumbitStep}
        endIcon={headerSlot ? rtl ? <ArrowLeft size={15} /> : <ArrowRight size={15} /> : undefined}
      >
        {headerSlot ? t.nextShort : t.next}
      </Button>
    </div>
  )

  return (
    <>
      <ConfirmationPopup
        isOpen={removalPending}
        isLoading={busy}
        title={t.confirmRemove}
        description={t.confirmRemoveHint}
        cancelButtonText={t.cancel}
        confirmButtonText={t.remove}
        onCancel={cancelRemove}
        onConfirm={confirmRemove}
      />
      {headerSlot && createPortal(nextAction, headerSlot)}
      <div
        className={`creation-builder${selected || categoryEditor ? ' has-editor' : ''}${
          onboarding ? ' is-onboarding' : ''
        }${selectedAi ? ' has-ai-editor' : ''}${categoryGrouping ? ' is-grouped' : ''}${
          categoryGrouping && naming ? ' is-category-intro' : ''
        }`}
        dir={rtl ? 'rtl' : 'ltr'}
      >
        <div className="creation-builder-columns">
          <section className="creation-list-panel">
            {!naming && taskTitle}
            <DragDropContext onDragEnd={categoryGrouping?.onReorder || reorder}>
              <Droppable droppableId="creation-subtasks">
                {(provided) => (
                  <div
                    className="creation-subtask-list"
                    data-empty={!fields.length}
                    ref={(node) => {
                      listRef.current = node
                      provided.innerRef(node)
                    }}
                    {...provided.droppableProps}
                  >
                    {!naming && categoryGrouping?.controls}
                    {naming &&
                      (categoryGrouping ? (
                        categoryGrouping.intro(taskTitle)
                      ) : (
                        <TaskNameStep taskName={taskTitle} ready={nameReady} disabled={busy} onStart={startBuilding} />
                      ))}
                    {/* Retained empty-list placeholder; the empty task now returns to choosing a type.
                      <SubtaskBuilderEmptyState variant="list" />
                    */}
                    {(() => {
                      const renderItem = (index: number, dragIndex = index) => {
                        const field = fields[index]
                        return (
                          <SubtaskPreviewCard
                            key={field.id}
                            sortable={!isEdit}
                            removable={!isEdit}
                            field={field}
                            draft={values.sub_tasks?.[index]}
                            index={dragIndex}
                            grouped={!!categoryGrouping}
                            isAi={aiIds.current.has(field.id!)}
                            isSelected={field.id === selectedId}
                            hasError={!!errors.sub_tasks?.[index]}
                            busy={busy}
                            rtl={rtl}
                            Icon={
                              buttons.find(
                                (button) =>
                                  button.type === (aiIds.current.has(field.id!) ? AI_IMAGE_BUTTON_TYPE : field.type)
                              )?.Component
                            }
                            onSelect={() => {
                              categoryGrouping?.onSelectSubtask?.(index)
                              select(field.id!)
                            }}
                            onRemove={() => requestRemove(index)}
                          />
                        )
                      }
                      return categoryGrouping
                        ? categoryGrouping.renderItems(renderItem, {
                            buttons,
                            onAdd: handleAddSubtask,
                            clearEditor: () => setSelectedId(null),
                          })
                        : fields.map((_, index) => renderItem(index))
                    })()}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
            {!isEdit && (!categoryGrouping || !naming) && (
              <SubtaskDock
                buttons={buttons}
                existingTypes={fields.map((field) =>
                  aiIds.current.has(field.id!) ? AI_IMAGE_BUTTON_TYPE : field.type
                )}
                onAdd={handleAddSubtask}
                hint={
                  categoryGrouping ? (
                    categoryGrouping.dockHint
                  ) : onboarding ? (
                    <span
                      className="creation-onboarding-pick"
                      data-disabled={addingDisabled}
                      data-active={!addingDisabled}
                    >
                      <span className="creation-pick-number" aria-hidden="true">
                        {categoryGrouping ? 3 : 2}
                      </span>
                      <span className="creation-pick-label">{t.pickType}</span>
                      {!addingDisabled && (
                        <ArrowDown className="creation-pick-arrow" size={34} strokeWidth={2.5} aria-hidden="true" />
                      )}
                    </span>
                  ) : undefined
                }
                disabled={addingDisabled}
                rtl={rtl}
                appearance={categoryGrouping ? 'light' : undefined}
              />
            )}
          </section>
          {categoryEditor && (
            <section className="creation-editor-panel">
              <div className="creation-editor-scroll">{categoryEditor}</div>
            </section>
          )}
          <section className="creation-editor-panel" style={categoryEditor ? { display: 'none' } : undefined}>
            {selected && !selectedAi && (
              <SubtaskCategoryHeader category={selectedCategory} title={selectedTitle}>
                <Button
                  variant="icon"
                  height="auto"
                  className="creation-editor-back"
                  type="button"
                  isDisabled={busy}
                  aria-label={t.back}
                  onClick={() => setSelectedId(null)}
                >
                  {rtl ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
                </Button>
                <div className="creation-editor-heading creation-subtask-heading">
                  {SelectedIcon && (
                    <span className="creation-heading-icon">
                      <SelectedIcon />
                    </span>
                  )}
                  {selectedType === keys.SUBTASK_TYPES.DESCRIPTION ? (
                    <strong>{t.description}</strong>
                  ) : (
                    <>
                      <small>{t[subtaskTypeKeys[selectedType]]}</small>
                      <PreviewTitle value={selectedTitle} fallback={t.untitled} />
                    </>
                  )}
                </div>
                {!isEdit && selectedType !== keys.SUBTASK_TYPES.DESCRIPTION && (
                  <BuilderWidgetProvider categoryManagedExternally={!!categoryGrouping}>
                    <WidgetIsRequiredSwitch subtaskPath={`sub_tasks[${selectedIndex}]`} compact />
                  </BuilderWidgetProvider>
                )}
              </SubtaskCategoryHeader>
            )}
            <div className="creation-editor-scroll" hidden={selectedAi} ref={editorRef}>
              {/* Retained empty-editor placeholder; replaced by TaskLivePreview when no items remain.
                <SubtaskBuilderEmptyState variant="editor" hasSubtasks={false} />
              */}
              {!selected &&
                (onboarding || categoryGrouping ? (
                  <TaskLivePreview
                    groups={categoryGrouping?.previewGroups}
                    showOptions={!categoryGrouping}
                    activeCategory={categoryGrouping?.activeCategory}
                    title={naming && !categoryGrouping ? '' : values.title || ''}
                    buttons={buttons}
                    disabled={addingDisabled}
                    onAdd={handleAddSubtask}
                  />
                ) : (
                  <SubtaskBuilderEmptyState variant="editor" hasSubtasks={!!fields.length} showHint={!isEdit} />
                ))}
              <BuilderWidgetProvider categoryManagedExternally={!!categoryGrouping}>
                {/* Keep ALL widgets mounted: RHF v5 unregisters fields and upload state on unmount. */}
                {fields.map((field, index) => (
                  <fieldset
                    key={field.id}
                    hidden={selectedAi || field.id !== selectedId}
                    className="creation-editor-fields"
                    disabled={isEdit && field.type !== keys.SUBTASK_TYPES.DESCRIPTION}
                    {...(isEdit && field.type !== keys.SUBTASK_TYPES.DESCRIPTION ? { inert: '' } : {})}
                  >
                    <SubtaskEditor field={field} index={index} remove={() => !busy && requestRemove(index)} />
                  </fieldset>
                ))}
              </BuilderWidgetProvider>
            </div>
            {selected && !selectedAi && (
              <footer className="creation-editor-footer">
                {!isEdit && (
                  <Button
                    px="14px"
                    py="9px"
                    bg="#fdf5f5"
                    color="#bf4944"
                    borderWidth="1px"
                    borderColor="#eccfcd"
                    _hover={{ bg: '#fdebea', borderColor: '#e5bdb9' }}
                    variant="secondary"
                    height="auto"
                    type="button"
                    className="creation-delete"
                    isDisabled={busy}
                    onClick={() => requestRemove(selectedIndex)}
                  >
                    {t.remove}
                  </Button>
                )}
                <small>{t.doneHint}</small>
                <Button
                  px="24px"
                  py="9px"
                  borderWidth="1px"
                  borderColor="#343a3e"
                  color="#22272a"
                  variant="secondary"
                  height="auto"
                  type="button"
                  className="creation-done"
                  isDisabled={busy}
                  onClick={finish}
                >
                  {t.done}
                </Button>
              </footer>
            )}
            {fields.map((field, index) =>
              !isEdit && aiIds.current.has(field.id!) ? (
                <VmAiEditor
                  key={field.id}
                  id={field.id!}
                  index={index}
                  active={field.id === selectedId && !categoryEditor}
                  category={categoryGrouping ? values.sub_tasks?.[index]?.category : undefined}
                  title={values.sub_tasks?.[index]?.title}
                  onBusyChange={setAiGenerating}
                  onClose={() => setSelectedId(null)}
                  onRemove={() => requestRemove(index)}
                  onDone={finish}
                />
              ) : null
            )}
          </section>
        </div>
        {!headerSlot && nextAction}
      </div>
    </>
  )
}
